/**
 * Allocate an RGBA8 mask/source target on the caller's WebGL2 context.
 * Call within the engine's GL state transaction. This helper never queries or
 * restores host state; it only touches texture unit 0 and the draw framebuffer.
 */
export function createTarget(gl, width, height, { stencil = false, filter = gl.LINEAR } = {}) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new RangeError('GPU target dimensions must be positive and finite.');
  }
  width = Math.max(1, Math.ceil(width));
  height = Math.max(1, Math.ceil(height));
  const framebuffer = gl.createFramebuffer();
  const texture = gl.createTexture();
  const stencilBuffer = stencil ? gl.createRenderbuffer() : null;
  let destroyed = false;
  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    if (framebuffer) gl.deleteFramebuffer(framebuffer);
    if (texture) gl.deleteTexture(texture);
    if (stencilBuffer) gl.deleteRenderbuffer(stencilBuffer);
  };
  try {
    if (!framebuffer || !texture || (stencil && !stencilBuffer)) throw new Error('Could not allocate a brush GPU target.');
    // A host may have a pixel unpack buffer and non-default row layout bound.
    // Immutable texture storage allocates without consulting either of them.
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, width, height);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    if (stencilBuffer) {
      gl.bindRenderbuffer(gl.RENDERBUFFER, stencilBuffer);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.STENCIL_INDEX8, width, height);
      gl.framebufferRenderbuffer(gl.DRAW_FRAMEBUFFER, gl.STENCIL_ATTACHMENT, gl.RENDERBUFFER, stencilBuffer);
    }
    const status = gl.checkFramebufferStatus(gl.DRAW_FRAMEBUFFER);
    if (status !== gl.FRAMEBUFFER_COMPLETE) throw new Error(`Incomplete brush GPU target (${status}).`);
    // Clear explicitly; clear is unaffected by viewport and uses the new FBO.
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.RASTERIZER_DISCARD);
    gl.colorMask(true, true, true, true);
    gl.clearColor(0, 0, 0, 0);
    if (stencilBuffer) {
      gl.stencilMask(0xff);
      gl.clearStencil(0);
    }
    gl.clear(gl.COLOR_BUFFER_BIT | (stencilBuffer ? gl.STENCIL_BUFFER_BIT : 0));
    return { framebuffer, texture, width, height, stencilBuffer, destroy };
  } catch (error) {
    destroy();
    throw error;
  }
}
